import * as React from 'react'

import * as PopoverPrimitive from '@radix-ui/react-popover'
import { useComposedRefs } from '@radix-ui/react-compose-refs'
import { suppressOthers } from 'aria-hidden'

import { cn } from '../utils'

const Popover = PopoverPrimitive.Root

// Radix 1.1+ hardcodes aria-haspopup="dialog" (ARIA 1.2 only) and
// aria-controls pointing at an ID that only exists while the popover is open.
// Override both: aria-haspopup to the ARIA 1.1-safe "true", aria-controls
// removed (undefined) since the referenced element is absent when closed.
// aria-expanded remains — screen readers and focus management are unaffected.
const PopoverTrigger = React.forwardRef(({ ...props }, ref) => (
  <PopoverPrimitive.Trigger
    ref={ref}
    aria-haspopup="true"
    aria-controls={undefined}
    {...props}
  />
))
PopoverTrigger.displayName = PopoverPrimitive.Trigger.displayName

const PopoverCloseButton = PopoverPrimitive.Close

const PopoverContent = React.forwardRef(
  (
    { className, align = 'center', portal = true, sideOffset = 4, ...props },
    ref,
  ) => {
    const localRef = React.useRef(null)
    const composedRef = useComposedRefs(ref, localRef)

    React.useEffect(() => {
      if (!localRef.current) return
      return suppressOthers(localRef.current)
    }, [])

    const content = (
      <PopoverPrimitive.Content
        ref={composedRef}
        align={align}
        sideOffset={sideOffset}
        className={cn(
          'z-[1100] w-72 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
          className,
        )}
        {...props}
      />
    )

    if (!portal) return content

    return (
      <PopoverPrimitive.Portal
        container={document.getElementById('portal-root') ?? undefined}
      >
        {content}
      </PopoverPrimitive.Portal>
    )
  },
)
PopoverContent.displayName = PopoverPrimitive.Content.displayName

export { Popover, PopoverTrigger, PopoverContent, PopoverCloseButton }
